import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { authStart } from '@ghostwright/api-client';

const RESEND_COOLDOWN_SECONDS = 30;

export default function AuthSent() {
  const location = useLocation();
  const navigate = useNavigate();
  const email = (location.state as { email?: string } | null)?.email;

  const [countdown, setCountdown] = useState(RESEND_COOLDOWN_SECONDS);
  const [resending, setResending] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);

  // Redirect if no email in state
  useEffect(() => {
    if (!email) {
      navigate('/sign-in', { replace: true });
    }
  }, [email, navigate]);

  // Countdown timer
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  const handleResend = useCallback(async () => {
    if (!email || countdown > 0 || resending) return;
    setResending(true);
    setResendError(null);

    try {
      await authStart(email);
      setCountdown(RESEND_COOLDOWN_SECONDS);
    } catch {
      setResendError('Could not resend. Please try again.');
    } finally {
      setResending(false);
    }
  }, [email, countdown, resending]);

  if (!email) return null;

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-border px-5 sm:px-8 py-4">
        <div className="max-w-container mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 text-heading">
            <span className="font-display text-lg">Ghostwright</span>
            <span className="text-primary leading-none" aria-hidden="true">●</span>
          </a>
        </div>
      </header>

      <main className="flex-1 px-5 sm:px-8 py-12 sm:py-24">
        <div className="max-w-[480px] mx-auto">
          <div className="bg-card border border-border rounded-card p-8 text-center">
            {/* Mail icon */}
            <div
              className="w-12 h-12 mx-auto mb-5 rounded-full bg-primary-muted text-primary inline-flex items-center justify-center"
              aria-hidden="true"
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 22 22"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="5" width="16" height="12" rx="2" />
                <path d="M3 7l8 5 8-5" />
              </svg>
            </div>

            <h1 className="font-display italic text-[22px] text-foreground mb-3 [font-variation-settings:'opsz'_96,'SOFT'_80]">
              Check your email.
            </h1>

            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              We sent a sign-in link to
            </p>

            <span className="inline-block font-medium text-foreground py-1.5 px-3 bg-background border border-border rounded-chip text-[13px] mb-4">
              {email}
            </span>

            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              The link works on any device. If you don't see it in a minute, check spam.
            </p>

            {resendError && (
              <p className="text-sm text-destructive mb-3" role="alert">
                {resendError}
              </p>
            )}

            <p className="text-[13px] text-muted-foreground">
              Wrong email?{' '}
              <Link
                to="/sign-in"
                className="text-foreground underline decoration-border underline-offset-[3px]"
              >
                Send to a different one
              </Link>
              {' · or '}
              <button
                type="button"
                onClick={handleResend}
                disabled={countdown > 0 || resending}
                className="text-foreground underline decoration-border underline-offset-[3px] disabled:text-muted-foreground disabled:no-underline disabled:cursor-default"
              >
                {resending
                  ? 'Sending...'
                  : countdown > 0
                    ? `Resend (${countdown}s)`
                    : 'Resend'}
              </button>
            </p>
          </div>
        </div>
      </main>

      <footer className="border-t border-border px-5 sm:px-8 py-6 text-xs text-muted-foreground">
        <div className="max-w-container mx-auto flex items-center justify-between flex-wrap gap-3">
          <span>
            Ghostwright<span className="text-primary mx-1.5" aria-hidden="true">●</span>2026
          </span>
        </div>
      </footer>
    </div>
  );
}
